/**
 * Module-level singleton IntersectionObserver shared by every impression target.
 * Generic "fire a callback once when an element has dwelled in the viewport".
 *
 * One observer object for all cards (cheap), no React provider/context. The hook
 * `useNectarImpression` is the only consumer; lifecycle (observe/unobserve) rides
 * the component's mount/unmount.
 */

// Fraction of the element that must be in view before the dwell timer starts.
const IMPRESSION_THRESHOLD = 0.5;
// How long an element must stay in view before it counts (guards scroll thrash).
const IMPRESSION_DWELL_MS = 2000;

interface Target {
  onImpression: () => void;
  timer?: ReturnType<typeof setTimeout>;
}

const targets = new Map<Element, Target>();
let observer: IntersectionObserver | null = null;

const getObserver = (): IntersectionObserver | null => {
  if (typeof window === 'undefined' || !window.IntersectionObserver) return null;

  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const target = targets.get(entry.target);
          if (!target) continue;

          if (!entry.isIntersecting) {
            clearTimeout(target.timer);
            target.timer = undefined;
            continue;
          }

          // Already counting down for this element — don't restart the timer.
          if (target.timer) continue;

          target.timer = setTimeout(() => {
            target.onImpression();
            // Fire once per element: stop observing after it counts.
            unobserveImpression(entry.target);
          }, IMPRESSION_DWELL_MS);
        }
      },
      { threshold: IMPRESSION_THRESHOLD },
    );
  }

  return observer;
};

/** Start watching `element`; calls `onImpression` once it dwells in the viewport. */
export const observeImpression = (element: Element, onImpression: () => void): void => {
  const io = getObserver();
  if (!io) return;

  targets.set(element, { onImpression });
  io.observe(element);
};

/** Stop watching `element` and cancel any pending dwell timer. */
export const unobserveImpression = (element: Element): void => {
  const target = targets.get(element);
  if (target?.timer) clearTimeout(target.timer);
  targets.delete(element);
  observer?.unobserve(element);
};
